/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  useSDMCartContext,
  useSDMCustomerContext,
  useSDMViewportContext,
  type ISDMContextsProps,
} from '@ldp/sdm-contexts';
import * as Sentry from '@sentry/react';
import { useRouter } from 'next/router';
import React, { useEffect, type FC } from 'react';

// @ts-ignore: federated module
import { useCommonContextProps, useContentfulPreview, useTrackPageView } from '@/hooks';

import { Spinner } from '@/components/shared';
import { SdmProvince, VIEW_RESOLVER } from '@/data/constants';
import { ViewResolverSection } from '@/packages/ldp-core-sdm/src/components';
import ViewResolverSdmComponent from '@/packages/ldp-core-sdm/src/view-resolver-sdm.component';
import { MastheadSSRWrapper } from '../masthead/masthead-ssr-wrapper';
import PageLayoutSSR from '../page-layout/page-layout-ssr';
import { IViewResolverComponentProps } from '../view-resolver/view-resolver.interface';

/** This describes how the federated module can be used in legacy apps */
export const HeliosViewResolverComponentSSR: FC<IViewResolverComponentProps & { enableSSR?: boolean }> = ({
  initialData,
  themeTokens,
  headerSection,
  ...rest
}) => {
  const { renderDatePicker, isContentfulPreview, selectedDateTime } = useContentfulPreview();
  const router = useRouter();

  // Either text or promotionCodes is required for search page, redirect to home page if both are missing
  if (router.pathname === '/search' && !router.query?.text && !router.query?.promotionCodes) {
    router.push('/');
  }

  const { commonContextProps, locale, componentError } = useCommonContextProps();

  const sdmContextProps: ISDMContextsProps = {
    sdmCartProviderProps: {
      ...useSDMCartContext(),
    },
    sdmCustomerProviderProps: {
      ...useSDMCustomerContext(),
    },
    sdmRouterProps: { router },
    sdmViewportProviderProps: {
      ...useSDMViewportContext(),
    },
    provinceProviderProps: { children: null },
  };

  //analytics
  const { cartCount, isInitialCartCountUpdated, setPageId } = useSDMCartContext();
  const { pcOptimumAccount } = useSDMCustomerContext();
  const { pageId } = rest;

  useEffect(() => {
    setPageId(pageId);
  }, [pageId, setPageId]);

  useTrackPageView(cartCount, pageId, pcOptimumAccount!, isInitialCartCountUpdated);

  /** Capture Masthead Error in Sentry */
  useEffect(() => {
    if (componentError?.mastheadError) {
      Sentry.captureMessage(componentError.mastheadError);
    }
  }, [componentError]);

  return (
    <>
      <ViewResolverSdmComponent
        {...rest}
        hadExistingSentry={true}
        isPreview={isContentfulPreview}
        previewDatetime={selectedDateTime}
        commonContexts={commonContextProps}
        // @ts-ignore
        sdmContexts={sdmContextProps}
        initialData={initialData}
        themeTokens={themeTokens}
        PageLoadingFallback={() => <Spinner />}
        locale={locale}
        // @ts-ignore
        env={process.env.NEXT_PUBLIC_VIEW_RESOLVER_ENV}
      >
        {/* <MastheadSSR headerSection={headerSection ? headerSection : { region: SdmProvince.ON }} /> */}
        <MastheadSSRWrapper headerSection={headerSection ? headerSection : { region: SdmProvince.ON }} />
        <PageLayoutSSR typeId={initialData ? initialData?.layout?.typeId : VIEW_RESOLVER.PAGE_LAYOUT_SINGLE_COLUMN} />
        <ViewResolverSection
          apiVersion={VIEW_RESOLVER.API_VERSION_V2}
          layoutSection={VIEW_RESOLVER.MAIN_CONTENT_COLLECTION}
          hasFooter
        />
      </ViewResolverSdmComponent>
      {isContentfulPreview ? renderDatePicker() : null}
    </>
  );
};

export default HeliosViewResolverComponentSSR;
